import { t } from '../../../shared/i18n/index.js';

/** Inline task titles shared by the desktop and mobile execution lists. */
export function createExecutionTitleEditor() {
  let list = null;
  let options = null;
  let events = null;
  let editing = null;
  const rowId = row => row.dataset.executionId || row.dataset.nodeId;
  const findRow = id => [...list.querySelectorAll('[data-execution-row]')].find(row => rowId(row) === id);

  const canEdit = id => list?.isConnected && options?.isCurrent() &&
    !options.isReadOnly(id) && options.find(id);

  function capture() {
    if (!list?.isConnected) return null;
    if (!editing) {
      const pencil = document.activeElement?.closest('[data-execution-rename]');
      return pencil && list.contains(pencil)
        ? { focusRowId: rowId(pencil.closest('[data-execution-row]')) } : null;
    }
    const { nodeId, executionId, original, input } = editing;
    return { nodeId, executionId, original, value: input.value, focused: document.activeElement === input,
      start: input.selectionStart, end: input.selectionEnd, direction: input.selectionDirection };
  }

  function finish(commit, restoreFocus = false) {
    if (!editing) return true;
    const { nodeId, original } = editing;
    const name = editing.input.value.trim();
    if (commit && canEdit(nodeId) && name && name !== original.trim()) {
      try {
        options.onRename(nodeId, name);
      } catch {
        // Keep the draft available if local storage rejects the write.
        if (editing) {
          editing.input.setCustomValidity(t('routines.list.titleSaveFailed'));
          editing.input.reportValidity();
        }
        return false;
      }
    }
    if (!editing) return true;
    const { input, title, pencil, inputEvents } = editing;
    editing = null; // A write may synchronously render and bind a new list.
    inputEvents.abort();
    input.replaceWith(title);
    const node = options.find(nodeId);
    title.textContent = node ? (node.name || node.text || t('common.untitled')) : t('routines.list.missingNode');
    pencil.hidden = false;
    if (restoreFocus) pencil.focus({ preventScroll: true });
    return true;
  }

  function begin(nodeId, saved = null, executionId = saved?.executionId || nodeId) {
    if (!finish(true)) return;
    if (!canEdit(nodeId)) return;
    const row = findRow(executionId);
    const title = row?.querySelector('[data-execution-title]');
    const pencil = row?.querySelector('[data-execution-rename]');
    if (!title || !pencil) return;
    const node = options.find(nodeId);
    const original = saved?.original ?? String(node.name || node.text || '');
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'execution-title-input';
    input.setAttribute('aria-label', t('routines.list.taskTitle'));
    input.setAttribute('enterkeyhint', 'done');
    input.value = saved?.value ?? original;
    const inputEvents = new AbortController();
    editing = { nodeId, executionId, original, input, title, pencil, inputEvents };
    title.replaceWith(input);
    pencil.hidden = true;
    input.addEventListener('keydown', event => {
      event.stopPropagation();
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === 'Enter' || event.key === 'Escape') {
        event.preventDefault();
        finish(event.key === 'Enter', true);
      }
    }, { signal: inputEvents.signal });
    input.addEventListener('click', event => event.stopPropagation(), { signal: inputEvents.signal });
    input.addEventListener('input', () => input.setCustomValidity(''), { signal: inputEvents.signal });
    input.addEventListener('blur', () => finish(true), { signal: inputEvents.signal });
    if (!saved || saved.focused) {
      input.focus({ preventScroll: true });
      if (saved) input.setSelectionRange(saved.start, saved.end, saved.direction);
      else input.select();
    }
  }

  function dispose() {
    // Abort blur before replacing the DOM: rerenders must never save drafts.
    events?.abort();
    editing?.inputEvents.abort();
    editing = null;
    list = null;
  }

  function bind(nextList, nextOptions, saved = null) {
    dispose();
    list = nextList;
    options = nextOptions;
    if (!list) return;
    events = new AbortController();
    // Keep focus until click so switching tasks can finish the old editor
    // and find the new row even when that save triggers a synchronous render.
    list.addEventListener('pointerdown', event => {
      if (event.target.closest('[data-execution-rename], [data-execution-title]')) event.preventDefault();
    }, { signal: events.signal });
    list.addEventListener('click', event => {
      const button = event.target.closest('[data-execution-rename], [data-execution-title]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      const nodeId = button.closest('[data-execution-row]').dataset.nodeId;
      if (button.hasAttribute('data-execution-rename')) begin(nodeId, null, rowId(button.closest('[data-execution-row]')));
      else if (options.isCurrent() && finish(true)) options.onSelect(nodeId);
    }, { signal: events.signal });
    if (saved?.nodeId) begin(saved.nodeId, saved);
    else if (saved?.focusRowId) findRow(saved.focusRowId)?.querySelector('[data-execution-rename]')?.focus({ preventScroll: true });
  }

  return { capture, bind, dispose };
}
